import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { callsService } from '../../../utils/callsService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const CallSubmissionForm = ({ onSubmit, onAutoSave, savedData }) => {
  const { user, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    insurance_type: 'auto',
    call_quality: 'standard',
    customer_age: '',
    customer_zip_code: '',
    estimated_premium: '',
    reserve_price: '25.00',
    call_duration: '',
    call_notes: '',
    demographic_data: {}
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (savedData) {
      setFormData(savedData);
    }
  }, [savedData]);

  useEffect(() => {
    const autoSaveTimer = setTimeout(() => {
      if (Object.keys(formData)?.some(key => formData?.[key])) {
        onAutoSave?.(formData);
      }
    }, 5000);

    return () => clearTimeout(autoSaveTimer);
  }, [formData, onAutoSave]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.insurance_type) newErrors.insurance_type = 'Insurance type is required';
    if (!formData?.customer_age || formData?.customer_age < 18) newErrors.customer_age = 'Valid customer age is required (18+)';
    if (!formData?.customer_zip_code || formData?.customer_zip_code?.length < 5) newErrors.customer_zip_code = 'Valid ZIP code is required';
    if (!formData?.estimated_premium || formData?.estimated_premium <= 0) newErrors.estimated_premium = 'Estimated premium must be greater than $0';
    if (!formData?.reserve_price || formData?.reserve_price <= 0) newErrors.reserve_price = 'Reserve price must be greater than $0';

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!isAuthenticated) {
      setErrors({ general: 'Please sign in to submit calls' });
      return;
    }

    if (!validateForm()) return;

    try {
      setLoading(true);
      setErrors({});

      // ✅ REPLACED: Remove mock submission, use real Supabase call
      const result = await callsService?.submitCall({
        ...formData,
        customer_age: parseInt(formData?.customer_age),
        estimated_premium: parseFloat(formData?.estimated_premium),
        reserve_price: parseFloat(formData?.reserve_price),
        call_duration: formData?.call_duration ? parseInt(formData?.call_duration) : null
      });

      // Clear form on successful submission
      setFormData({
        insurance_type: 'auto',
        call_quality: 'standard',
        customer_age: '',
        customer_zip_code: '',
        estimated_premium: '',
        reserve_price: '25.00',
        call_duration: '',
        call_notes: '',
        demographic_data: {}
      });

      // Notify parent component
      onSubmit?.(result);

    } catch (error) {
      setErrors({ 
        general: error?.message || 'Failed to submit call. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="text-center py-8">
          <Icon name="Lock" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">Authentication Required</h3>
          <p className="text-muted-foreground mb-4">Please sign in to submit calls to the marketplace</p>
          <Button 
            variant="default"
            onClick={() => window.location.href = '/signin'}
          >
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="p-6 border-b border-border">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="FileText" size={20} className="text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Submit New Call</h2>
            <p className="text-sm text-muted-foreground">Enter call details to start the bidding process</p>
          </div>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {errors?.general && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {errors?.general}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Insurance Type *
            </label>
            <Select
              value={formData?.insurance_type}
              onChange={(value) => handleInputChange('insurance_type', value)}
              options={[
                { value: 'auto', label: 'Auto Insurance' },
                { value: 'home', label: 'Home Insurance' },
                { value: 'life', label: 'Life Insurance' },
                { value: 'health', label: 'Health Insurance' },
                { value: 'commercial', label: 'Commercial Insurance' },
                { value: 'other', label: 'Other' }
              ]}
              error={errors?.insurance_type}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Call Quality
            </label>
            <Select
              value={formData?.call_quality}
              onChange={(value) => handleInputChange('call_quality', value)}
              options={[
                { value: 'premium', label: 'Premium' },
                { value: 'standard', label: 'Standard' },
                { value: 'basic', label: 'Basic' }
              ]}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Customer Age *
            </label>
            <Input
              type="number"
              placeholder="35"
              value={formData?.customer_age}
              onChange={(value) => handleInputChange('customer_age', value)}
              error={errors?.customer_age}
              min="18"
              max="100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              ZIP Code *
            </label>
            <Input
              type="text"
              placeholder="90210"
              value={formData?.customer_zip_code}
              onChange={(value) => handleInputChange('customer_zip_code', value)}
              error={errors?.customer_zip_code}
              maxLength="10"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Estimated Premium *
            </label>
            <Input
              type="number"
              placeholder="1800.00"
              value={formData?.estimated_premium}
              onChange={(value) => handleInputChange('estimated_premium', value)}
              error={errors?.estimated_premium}
              step="0.01"
              min="0"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Reserve Price *
            </label>
            <Input
              type="number"
              placeholder="25.00"
              value={formData?.reserve_price}
              onChange={(value) => handleInputChange('reserve_price', value)}
              error={errors?.reserve_price}
              step="0.01"
              min="0"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Call Duration (seconds)
          </label>
          <Input
            type="number"
            placeholder="180"
            value={formData?.call_duration}
            onChange={(value) => handleInputChange('call_duration', value)}
            min="0"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Call Notes
          </label>
          <textarea
            className="w-full p-3 border border-border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            rows="3"
            placeholder="Additional notes about the call quality, customer interest level, etc."
            value={formData?.call_notes}
            onChange={(e) => handleInputChange('call_notes', e?.target?.value)}
          />
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="text-sm text-muted-foreground">
            * Required fields
          </div>
          
          <div className="flex space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setFormData({
                insurance_type: 'auto',
                call_quality: 'standard',
                customer_age: '',
                customer_zip_code: '',
                estimated_premium: '',
                reserve_price: '25.00',
                call_duration: '',
                call_notes: '',
                demographic_data: {}
              })}
              disabled={loading}
            >
              Clear
            </Button>
            
            <Button
              type="submit"
              variant="default"
              disabled={loading}
              iconName={loading ? "Loader2" : "Send"}
              iconPosition="left"
            >
              {loading ? 'Submitting...' : 'Submit Call'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CallSubmissionForm;